import { Link } from 'react-router-dom';
import { Heart, MapPin, ArrowRight, Quote } from 'lucide-react';

const IMAGES = {
  founder: "/images/founder-brianna.jpeg",
  event: "/images/hero-children.jpeg",
  kenya: "https://images.unsplash.com/photo-1650419260381-28e4bf578e31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxrZW55YSUyMGxhbmRzY2FwZSUyMHN1bnJpc2UlMjBjb21tdW5pdHl8ZW58MHx8fHwxNzc0NTY5OTIwfDA&ixlib=rb-4.1.0&q=85"
};

const AboutPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#FDFBF7]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="font-accent text-2xl text-[#FF7A59]">About Protopia</span>
            <h1 className="text-5xl md:text-6xl font-bold text-[#1E293B] font-['Fredoka'] mt-2 mb-6">
              Meet the Founder: <span className="text-[#38BDF8]">Brianna Thomas</span>
            </h1>
            <p className="text-xl text-[#64748B] leading-relaxed">
              The seeds of Protopia were sown long before the organization was born. 
              It began with two defining influences: the enduring legacy of her grandmother, 
              Wilma Jean, and the profound resilience of her daughter.
            </p>
          </div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-24 bg-white" data-testid="founder-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={IMAGES.founder} 
                  alt="Brianna Thomas, Founder of Protopia" 
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 bg-[#FACC15] rounded-full w-32 h-32 flex items-center justify-center shadow-lg">
                <Heart className="w-12 h-12 text-[#422006]" />
              </div>
              {/* Fun in the Sun Event Info */}
              <div className="mt-12 bg-[#38BDF8]/10 rounded-2xl p-6 border-l-4 border-[#38BDF8]">
                <h3 className="text-xl font-bold text-[#1E293B] font-['Fredoka'] mb-3">
                  Fun in the Sun Events
                </h3>
                <p className="text-[#64748B] leading-relaxed">
                  We host <span className="font-bold text-[#38BDF8]">big Fun in the Sun events in Nairobi, Kenya twice a year</span> to 
                  support children with talents. Our biggest hope is to <span className="font-bold text-[#FF7A59]">inspire and motivate</span> the 
                  next generation to dream big and believe in themselves.
                </p>
              </div>
            </div>
            
            <div>
              <div className="flex items-start gap-4 mb-8">
                <Quote className="w-12 h-12 text-[#FACC15] flex-shrink-0" />
                <p className="text-2xl font-accent text-[#1E293B] italic">
                  "The sky is only the limit if you can't see past it"
                </p>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-[#1E293B] font-['Fredoka'] mb-6">
                A Legacy of Giving
              </h2>
              <p className="text-lg text-[#64748B] leading-relaxed mb-6">
                Growing up, Brianna's summers were defined by "Fun in the Sun"—a local gathering 
                where children could find joy and receive the school supplies they needed to succeed. 
                This beautiful tradition was created by her grandmother, Wilma Jean.
              </p>
              <p className="text-lg text-[#64748B] leading-relaxed mb-6">
                After her grandmother passed away, Brianna felt a calling to keep that spirit of 
                community alive. She envisioned a "master plan" that would combine her professional 
                drive with her deep, lifelong love for Africa—its music, its heart, and its people.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Born from Resilience */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="resilience-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block px-4 py-2 bg-[#FF7A59]/10 text-[#FF7A59] font-semibold rounded-full mb-6">
                The Catalyst
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1E293B] font-['Fredoka'] mb-6">
                Born from Resilience
              </h2>
              <p className="text-lg text-[#64748B] leading-relaxed mb-6">
                The final spark for Protopia came during a period of immense personal challenge. 
                When Brianna's daughter was born at just 22 weeks, the family found refuge through 
                the Ronald McDonald House Charities.
              </p>
              <p className="text-lg text-[#64748B] leading-relaxed mb-6">
                The kindness they experienced during their time in the NICU ignited a fire in 
                Brianna to do something "big for the world." She knew she had to pay forward the 
                compassion that had carried her family through their darkest days.
              </p>
              <div className="bg-white rounded-2xl p-6 border-l-4 border-[#38BDF8]">
                <p className="text-[#1E293B] font-semibold">
                  "The kindness we received during our NICU journey ignited a fire to do something 
                  big for the world."
                </p>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={IMAGES.event} 
                  alt="Children at Fun in the Sun event" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Kenya */}
      <section className="py-24 bg-white" data-testid="why-kenya">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={IMAGES.kenya} 
                  alt="Kenya landscape" 
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#38BDF8] rounded-2xl p-6 shadow-lg flex items-center gap-3">
                <MapPin className="w-8 h-8 text-white" />
                <div>
                  <p className="text-white font-bold">Based in</p>
                  <p className="text-white/90">Nairobi, Kenya</p>
                </div>
              </div>
            </div>
            
            <div>
              <span className="font-accent text-2xl text-[#FF7A59]">Our Home</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1E293B] font-['Fredoka'] mt-2 mb-6">
                Why Kenya?
              </h2>
              <p className="text-lg text-[#64748B] leading-relaxed mb-6">
                Driven by a desire to give back to a continent that she felt a soulful connection to, 
                Brianna focused her research on East Africa. While exploring the needs of various nations, 
                Kenya stood out.
              </p>
              <p className="text-lg text-[#64748B] leading-relaxed mb-8">
                With its developing infrastructure and status as a welcoming hub for international NGOs, 
                Kenya became the clear choice for Protopia's inaugural projects. Today, Brianna is 
                bridging the gap between her roots and her vision.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/events"
                  data-testid="about-events-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-6 py-3 hover:-translate-y-1 transition-all duration-200"
                >
                  Our Programs
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/donate"
                  data-testid="about-donate-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#E2E8F0] bg-transparent text-[#1E293B] font-bold px-6 py-3 hover:bg-[#F3F4F6] transition-all duration-200"
                >
                  Support Our Mission
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-[#1E293B]" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white/5 backdrop-blur rounded-3xl p-10 border border-white/10">
              <h3 className="text-2xl font-bold text-[#FACC15] font-['Fredoka'] mb-6">
                Our Mission
              </h3>
              <p className="text-lg text-white/90 leading-relaxed">
                To empower, support, and restore dignity to underserved communities—especially children 
                with disabilities, those in foster care, and survivors of domestic abuse—through 
                compassionate programs, advocacy, and life-changing experiences rooted in hope and healing.
              </p>
            </div>
            
            <div className="bg-white/5 backdrop-blur rounded-3xl p-10 border border-white/10">
              <h3 className="text-2xl font-bold text-[#38BDF8] font-['Fredoka'] mb-6">
                Our Vision
              </h3>
              <p className="text-lg text-white/90 leading-relaxed">
                A world where every child and every family, regardless of their circumstances, has 
                access to love, care, and opportunity—where hope is restored, voices are heard, and 
                communities rise together through collective support and transformation.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
